// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewRejectScopePrompt.test.tsx
import { Button, Flex, Text } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  isAnswerableScope,
  rememberedSentence,
  type RejectionScope,
  type ScopeDimension,
} from "./rememberedSentence";

export type ScopeChoice = {
  /** The dimension this option scopes by, or undefined for a brand. */
  dimension?: ScopeDimension;
  value: string;
};

export type AskV2ReviewRejectScopePromptProps = {
  commentId: string;
  findingText: string;
  /** The brand the finding already names. When set, nothing is asked. */
  brand?: string;
  /** Tags the source rule carries, offered when there is no brand. */
  choices?: ScopeChoice[];
  onConfirm: (scope: RejectionScope) => void;
  onCancel: () => void;
};

/**
 * What Puntt will remember, shown before it remembers it.
 *
 * Two jobs in one moment, because the reviewer has 30 seconds and two
 * interruptions would cost more than the feature is worth:
 *
 * 1. Show the sentence. This is her only chance to catch Puntt about to learn
 *    the wrong thing, and it is worthless if she has to go looking for it.
 * 2. Ask for a scope ONLY when the finding names no brand. A rejection with no
 *    scope binds the whole enterprise, so the alternative to asking is
 *    guessing the widest possible answer on her behalf.
 *
 * When the finding already names a brand this renders a confirm step with no
 * question -- one click to reject, one to agree with what gets stored.
 */
export function AskV2ReviewRejectScopePrompt({
  commentId,
  findingText,
  brand,
  choices = [],
  onConfirm,
  onCancel,
}: AskV2ReviewRejectScopePromptProps) {
  const { t } = useTranslation("ui");
  const [picked, setPicked] = useState<ScopeChoice | null>(null);

  const scope: RejectionScope = brand?.trim()
    ? { brand: brand.trim() }
    : picked
      ? { dimension: picked.dimension, value: picked.value }
      : {};
  // Blocked only when there IS something to choose and she has not chosen it.
  // A finding that names no brand and carries no tags offers nothing to scope
  // by, and refusing the rejection there would strand her on a finding she
  // cannot dismiss. She still rejects it -- the row is recorded and hides the
  // card on this review -- and warhol's `is_answerable` refuses to LEARN from
  // it, so it never binds another artwork. The copy below says exactly that.
  const mustChoose = !brand?.trim() && choices.length > 0;
  const answerable = isAnswerableScope(scope) || !mustChoose;
  const titleId = `ask-v2-reject-scope-title-${commentId}`;

  return (
    <div
      role="group"
      aria-labelledby={titleId}
      data-testid={`ask-v2-reject-scope-prompt-${commentId}`}
      className="mt-3 rounded-lg border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 p-3"
    >
      <Text
        as="p"
        size="1"
        weight="medium"
        id={titleId}
        data-testid={`ask-v2-reject-scope-heading-${commentId}`}
      >
        {t("assetViewer.rejectScopeHeading")}
      </Text>

      <Text
        as="p"
        size="1"
        mt="1"
        data-testid={`ask-v2-reject-remembered-${commentId}`}
        className="text-puntt-neutral-gray-11"
      >
        {rememberedSentence(findingText, scope)}
      </Text>

      {!brand?.trim() && (
        <>
          <Text
            as="p"
            size="1"
            mt="2"
            data-testid={`ask-v2-reject-scope-question-${commentId}`}
            className="text-puntt-neutral-gray-11"
          >
            {t("assetViewer.rejectScopeQuestion")}
          </Text>
          <Flex gap="1" wrap="wrap" mt="1" asChild>
            <div>
              {choices.map((choice) => {
                const id = `${choice.dimension ?? "brand"}-${choice.value}`;
                const selected =
                  picked?.value === choice.value &&
                  picked?.dimension === choice.dimension;
                return (
                  <Button
                    key={id}
                    type="button"
                    size="1"
                    variant={selected ? "solid" : "outline"}
                    color="gray"
                    aria-pressed={selected}
                    data-testid={`ask-v2-reject-scope-option-${commentId}-${id}`}
                    onClick={() => setPicked(selected ? null : choice)}
                  >
                    {choice.value}
                  </Button>
                );
              })}
            </div>
          </Flex>
          {choices.length === 0 && (
            <Text
              as="p"
              size="1"
              mt="1"
              data-testid={`ask-v2-reject-scope-none-${commentId}`}
              className="text-puntt-neutral-gray-9"
            >
              {t("assetViewer.rejectScopeNone")}
            </Text>
          )}
        </>
      )}

      <Flex gap="2" mt="3">
        <Button
          type="button"
          size="1"
          variant="solid"
          color="gray"
          disabled={!answerable}
          data-testid={`ask-v2-reject-confirm-${commentId}`}
          onClick={() => onConfirm(scope)}
        >
          {t("askV2.review.deleteFindingConfirm")}
        </Button>
        <Button
          type="button"
          size="1"
          variant="outline"
          color="gray"
          data-testid={`ask-v2-reject-cancel-${commentId}`}
          onClick={onCancel}
        >
          {t("assetViewer.rejectCancel")}
        </Button>
      </Flex>
    </div>
  );
}
